import {
  DTOValueExerciseMatrix,
  DTOValueSkip,
  QuestionnaireCategoryItemsItem,
  QuestionnaireCategoryItemsItemOneOf,
  QuestionnaireResponseStatus,
  UpdateQuestionnaireResponseAnswerInput,
} from '@/api/generated-catch-backend/model';
import {
  getListQuestionnaireResponsesV2QuestionnairesQuestionnaireIdResponsesGetQueryKey,
  useGetQuestionnaireByIdV2QuestionnairesQuestionnaireIdGet,
  useGetQuestionnaireResponseQuestionV2QuestionnairesQuestionnaireIdResponsesResponseIdQuestionsQuestionSlugGet,
  useGetQuestionnaireResponseWithAnswersV2QuestionnairesQuestionnaireIdResponsesResponseIdAnswersGet,
  useListQuestionnaireResponseQuestionsV2QuestionnairesQuestionnaireIdResponsesResponseIdQuestionsGet,
  useListQuestionnaireResponsesV2QuestionnairesQuestionnaireIdResponsesGet,
  useListQuestionnairesV2QuestionnairesGet,
  useStartQuestionnaireResponseV2QuestionnairesQuestionnaireIdResponsesPost,
  useSubmitQuestionnaireResponseV2QuestionnairesQuestionnaireIdResponsesResponseIdPatch,
  useUpdateQuestionnaireResponseAnswerV2QuestionnairesQuestionnaireIdResponsesResponseIdQuestionsQuestionSlugAnswersPut,
} from '@/api/generated-catch-backend/questionnaire/questionnaire';
import { queryClient } from '@/client/query';
import { isValidEmail } from '@/utils/common';
import React from 'react';

/**
 * Hook to get a specific questionnaire by ID
 */
export const useGetQuestionnaire = (questionnaireId: string, enabled = true) => {
  return useGetQuestionnaireByIdV2QuestionnairesQuestionnaireIdGet(questionnaireId, {
    query: {
      enabled: !!questionnaireId && enabled,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  });
};

/**
 * Hook to list all available questionnaires
 */
export const useListQuestionnaires = (enabled = true) => {
  return useListQuestionnairesV2QuestionnairesGet(undefined, {
    query: {
      enabled: enabled,
      staleTime: 10 * 60 * 1000, // 10 minutes - questionnaires don't change often
      gcTime: 15 * 60 * 1000, // 15 minutes cache time
      retry: 2,
      retryDelay: 1000,
      refetchOnMount: false, // Only fetch once
    },
  });
};

/**
 * Hook to get existing questionnaire responses for a user
 */
export const useGetQuestionnaireResponses = (questionnaireId: string, enabled = true) => {
  return useListQuestionnaireResponsesV2QuestionnairesQuestionnaireIdResponsesGet(questionnaireId, undefined, {
    query: {
      enabled: !!questionnaireId && enabled,
      staleTime: 2 * 60 * 1000, // 2 minutes
      gcTime: 5 * 60 * 1000, // 5 minutes cache time
      retry: true,
    },
  });
};

/**
 * Helper function to find the most recent in-progress or completed response
 */
export const findExistingResponse = <T extends { status: QuestionnaireResponseStatus; updatedAtUtc: string }>(
  responses: T[],
): T | null => {
  if (!responses || responses.length === 0) return null;

  // First try to find an in-progress response
  const inProgressResponse = responses.find(
    (response) => response.status === QuestionnaireResponseStatus['in-progress'],
  );
  if (inProgressResponse) return inProgressResponse;

  // If no in-progress response, return the most recent completed one
  const completedResponses = responses.filter((response) => response.status === QuestionnaireResponseStatus.completed);
  if (completedResponses.length > 0) {
    return completedResponses.sort(
      (a, b) => new Date(b.updatedAtUtc).getTime() - new Date(a.updatedAtUtc).getTime(),
    )[0];
  }

  return null;
};

/**
 * Hook to get or create a questionnaire response session
 * This ensures only one response per user per questionnaire
 */
export const useGetOrCreateQuestionnaireResponse = (questionnaireId: string) => {
  const { data: questionnaire, isLoading: isLoadingQuestionnaire } = useGetQuestionnaire(questionnaireId);
  const { data: existingResponses, isLoading: isLoadingResponses } = useGetQuestionnaireResponses(questionnaireId);

  const startMutation = useStartQuestionnaireResponseV2QuestionnairesQuestionnaireIdResponsesPost({
    mutation: {
      onSuccess: () => {
        // Invalidate the responses cache
        const queryKey =
          getListQuestionnaireResponsesV2QuestionnairesQuestionnaireIdResponsesGetQueryKey(questionnaireId);
        queryClient.invalidateQueries({ queryKey });
      },
    },
  });

  // Find existing response
  const existingResponse = existingResponses?.data ? findExistingResponse(existingResponses.data) : null;

  const getOrCreateResponse = React.useCallback(async () => {
    // If we have an existing response, use it
    if (existingResponse) {
      return {
        questionnaireResponseId: existingResponse.id,
        isNew: false,
        status: existingResponse.status,
      };
    }

    // Otherwise create a new response
    if (!questionnaire?.version) {
      throw new Error('Questionnaire version is required to start a new response');
    }

    try {
      const result = await startMutation.mutateAsync({
        questionnaireId,
        data: { questionnaireVersion: questionnaire.version },
      });
      return {
        questionnaireResponseId: result.questionnaireResponseId,
        isNew: true,
        status: QuestionnaireResponseStatus['in-progress'],
        firstQuestionSlug:
          result.firstQuestion && 'slug' in result.firstQuestion ? result.firstQuestion.slug : undefined,
      };
    } catch (error: any) {
      // Handle 409 Conflict - response already exists
      if (error?.response?.status === 409) {
        // Check for structured error response
        const errorData = error?.response?.data;
        if (errorData?.error?.details) {
          // Look for existing response ID in error details
          for (const detail of errorData.error.details) {
            if (detail.message && detail.message.includes('Existing response ID:')) {
              const uuidPattern = /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/i;
              const match = detail.message.match(uuidPattern);

              if (match && match[0]) {
                return {
                  questionnaireResponseId: match[0],
                  isNew: false,
                  status: QuestionnaireResponseStatus['in-progress'],
                };
              }
            }
          }
        }

        // Fallback: Parse the error detail string to extract existing response ID
        const errorDetail = error?.response?.data?.detail;
        if (errorDetail && typeof errorDetail === 'string') {
          // Look for UUID pattern in the error message
          const uuidPattern = /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/i;
          const match = errorDetail.match(uuidPattern);

          if (match && match[0]) {
            return {
              questionnaireResponseId: match[0],
              isNew: false,
              status: QuestionnaireResponseStatus['in-progress'],
            };
          }
        }

        // If we couldn't extract the ID from the 409 error, we know a response exists
        // Log it but don't break the flow - the UI will handle the lack of response gracefully
        console.warn(
          '409 Conflict: Response exists but ID not extractable from error. Continuing without response ID.',
        );

        // Return null to indicate we couldn't get or create a response
        // The calling code should handle this gracefully
        return null;
      }

      // Re-throw other errors
      throw error;
    }
  }, [existingResponse, startMutation, questionnaireId, questionnaire?.version]);

  return {
    getOrCreateResponse,
    isLoading: isLoadingResponses || isLoadingQuestionnaire || startMutation.isPending,
    error: startMutation.error,
    existingResponse,
    hasExistingResponse: !!existingResponse,
  };
};

/**
 * Hook to submit an answer for a specific question
 */
export const useSubmitAnswer = (questionnaireId?: string, onError?: (error: any) => void) => {
  return useUpdateQuestionnaireResponseAnswerV2QuestionnairesQuestionnaireIdResponsesResponseIdQuestionsQuestionSlugAnswersPut(
    {
      mutation: {
        onError,
      },
    },
  );
};

/**
 * Hook to submit/complete the entire questionnaire response
 */
export const useSubmitQuestionnaireResponse = (questionnaireId?: string) => {
  return useSubmitQuestionnaireResponseV2QuestionnairesQuestionnaireIdResponsesResponseIdPatch();
};

/**
 * Hook to list all questions answered in the current response with enhanced navigation context
 * This replaces the old useListResponseQuestions and provides server-driven session restoration
 */
export const useListResponseQuestions = (questionnaireId: string, responseId: string, enabled = false) => {
  return useListQuestionnaireResponseQuestionsV2QuestionnairesQuestionnaireIdResponsesResponseIdQuestionsGet(
    questionnaireId,
    responseId,
    undefined,
    {
      query: {
        enabled: !!questionnaireId && !!responseId && enabled,
      },
    },
  );
};

/**
 * Hook to get a questionnaire response with all submitted answers
 */
export const useGetQuestionnaireResponseWithAnswers = (
  questionnaireId: string,
  responseId: string,
  enabled = false,
) => {
  return useGetQuestionnaireResponseWithAnswersV2QuestionnairesQuestionnaireIdResponsesResponseIdAnswersGet(
    questionnaireId,
    responseId,
    {
      query: {
        enabled: !!questionnaireId && !!responseId && enabled,
        staleTime: 2 * 60 * 1000, // 2 minutes
        gcTime: 5 * 60 * 1000, // 5 minutes cache time
      },
    },
  );
};

/**
 * Helper function to determine if we should fetch from API or use mock data
 */
export const shouldUseMockData = (): boolean => {
  // This can be controlled via environment variable or feature flag
  return process.env.VITE_USE_MOCK_QUESTIONNAIRE === 'true';
};

/**
 * Helper function to format answer data for submission
 * Maps different value types to the appropriate DTO structure and validates form values.
 * Returns null and logs warnings for invalid values instead of throwing errors to prevent DataDog alerts.
 * This is a last line of defense and practically the form validation should catch the issues.
 */
export const formatAnswerForSubmission = ({
  value,
  questionType,
  units,
}: {
  value:
    | string
    | number
    | boolean
    | string[]
    | Record<string, string>
    | Record<string, string | number>
    | Record<string, Record<string, number>>;
  questionType: QuestionnaireCategoryItemsItemOneOf['questionType'];
  units?: string | null;
}): UpdateQuestionnaireResponseAnswerInput | null => {
  switch (questionType) {
    case 'text':
      if (typeof value !== 'string') {
        console.warn(`Invalid value for text question: ${value}`);
        return null;
      }
      return {
        value: [{ valueText: String(value) }],
      };
    case 'name-email': {
      if (
        typeof value !== 'object' ||
        value === null ||
        !('name' in value) ||
        !('email' in value) ||
        typeof value.name !== 'string' ||
        typeof value.email !== 'string'
      ) {
        console.warn(`Invalid value for name-email question: ${JSON.stringify(value)}`);
        return null;
      }
      // Check for empty strings
      if (value.name.trim() === '' || value.email.trim() === '') {
        console.warn(`Name and email are required and cannot be empty`);
        return null;
      }
      // Email validation
      if (!isValidEmail(value.email)) {
        console.warn(`Invalid email address: ${value.email}`);
        return null;
      }
      return {
        value: [
          { valueText: value.name, displayValue: 'name' },
          { valueText: value.email, displayValue: 'email' },
        ],
      };
    }
    case 'decimal':
      if (typeof value !== 'number') {
        console.warn(`Invalid value for decimal question: ${value}`);
        return null;
      }
      if (isNaN(value)) {
        console.warn(`Invalid decimal value: ${value}`);
        return null;
      }
      return {
        value: [{ valueFloat: value }],
      };
    case 'number':
    case 'number-stepper':
      if (typeof value !== 'number') {
        console.warn(`Invalid value for number question: ${value}`);
        return null;
      }
      if (isNaN(value)) {
        console.warn(`Invalid number value: ${value}`);
        return null;
      }
      if (!Number.isInteger(value)) {
        console.warn(`Number value must be an integer: ${value}`);
        return null;
      }
      return {
        value: [{ valueInt: value, unit: units }],
      };
    case 'radio':
    case 'select':
      if (typeof value !== 'string') {
        console.warn(`Invalid value for radio/select question: ${value}`);
        return null;
      }
      if (value.trim() === '') {
        console.warn(`Invalid value for radio/select question: empty string`);
        return null;
      }
      return {
        value: [{ valueString: value }],
      };
    case 'checkbox':
    case 'multiselect': {
      if (!Array.isArray(value)) {
        console.warn(`Invalid value for checkbox/multiselect question: ${value}`);
        return null;
      }
      if (value.length === 0) {
        console.warn(`Invalid value for checkbox/multiselect question: empty array`);
        return null;
      }
      if (value.some((v) => typeof v !== 'string')) {
        console.warn(`Invalid value for checkbox/multiselect question: all values must be strings`);
        return null;
      }
      const mappedValues = value.map((val) => ({ valueString: val }));
      return {
        value: mappedValues,
      };
    }
    case 'date':
      if (typeof value !== 'string' || isNaN(Date.parse(value))) {
        console.warn(`Invalid value for date question: ${value}`);
        return null;
      }
      if (new Date(value).toString() === 'Invalid Date') {
        console.warn(`Invalid date value: ${value}`);
        return null;
      }
      return {
        value: [{ valueDate: value }],
      };
    case 'height':
      if (typeof value !== 'number' || isNaN(value)) {
        console.warn(`Invalid value for height question: ${value}`);
        return null;
      }
      if (value <= 0) {
        console.warn(`Height must be a positive number: ${value}`);
        return null;
      }
      return {
        value: [{ valueFloat: value, unit: 'ft' }],
      };
    case 'weight':
      if (typeof value !== 'number' || isNaN(value)) {
        console.warn(`Invalid value for weight question: ${value}`);
        return null;
      }
      if (value <= 0) {
        console.warn(`Weight must be a positive number: ${value}`);
        return null;
      }
      return {
        value: [{ valueInt: value, unit: 'lbs' }],
      };
    case 'height-weight': {
      if (typeof value !== 'object' || value === null || !('height' in value) || !('weight' in value)) {
        console.warn(`Invalid value for height-weight question: ${JSON.stringify(value)}`);
        return null;
      }
      let { height, weight } = value as { height: number | string; weight: number | string };
      if (typeof height === 'string') {
        height = parseFloat(height);
      }
      if (typeof weight === 'string') {
        weight = parseInt(weight, 10);
      }
      if (typeof height !== 'number' || isNaN(height) || height <= 0) {
        console.warn(`Invalid height value: ${height}`);
        return null;
      }
      if (typeof weight !== 'number' || isNaN(weight) || weight <= 0) {
        console.warn(`Invalid weight value: ${weight}`);
        return null;
      }
      if (!Number.isInteger(weight)) {
        console.warn(`Height and weight must be integers: height=${height}, weight=${weight}`);
        return null;
      }
      return {
        value: [
          { valueFloat: height, unit: 'ft' },
          { valueInt: weight, unit: 'lbs' },
        ],
      };
    }
    case 'exercise-activity-matrix': {
      if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        console.warn(`Invalid value for exercise-activity-matrix question: ${JSON.stringify(value)}`);
        return null;
      }
      const isValidActivity = (activity: unknown): activity is { daysPerWeek: number; minutesPerDay: number } => {
        return (
          typeof activity === 'object' &&
          activity !== null &&
          'daysPerWeek' in activity &&
          'minutesPerDay' in activity &&
          typeof activity.daysPerWeek === 'number' &&
          typeof activity.minutesPerDay === 'number' &&
          activity.daysPerWeek >= 0 &&
          activity.minutesPerDay >= 0
        );
      };
      const isValidActivityMatrix = (
        matrix: Record<string, unknown>,
      ): matrix is Record<string, { daysPerWeek: number; minutesPerDay: number }> => {
        return Object.values(matrix).every(isValidActivity);
      };
      if (!isValidActivityMatrix(value)) {
        console.warn(`Invalid value for exercise-activity-matrix question: ${JSON.stringify(value)}`);
        return null;
      }
      const activities = value;
      const exerciseActivities = [];
      for (const [activity, metrics] of Object.entries(activities)) {
        if (metrics.daysPerWeek > 0 || metrics.minutesPerDay > 0) {
          exerciseActivities.push({
            activity,
            daysPerWeek: metrics.daysPerWeek,
            minutesPerDay: metrics.minutesPerDay,
          });
        }
      }

      const exerciseMatrixValue: DTOValueExerciseMatrix = {
        valueExerciseMatrix: {
          activities: exerciseActivities,
          version: '2025-07-29', // TODO: the backend doesn't save this yet.
        },
        displayValue: exerciseActivities
          .map((act) => `${act.activity}: ${act.daysPerWeek} days/week, ${act.minutesPerDay} min/day`)
          .join('; '),
      };

      return {
        value: [exerciseMatrixValue],
      };
    }
    case 'email-only': {
      if (typeof value !== 'string') {
        console.warn(`Invalid value for email-only question: ${value}`);
        return null;
      }
      if (value.trim() === '') {
        console.warn(`Email is required and cannot be empty`);
        return null;
      }
      // Email validation
      if (!isValidEmail(value)) {
        console.warn(`Invalid email address: ${value}`);
        return null;
      }
      return {
        value: [{ valueText: value }],
      };
    }
  }
};

/**
 * Helper function to format a skip request for submission
 * Used when user explicitly skips an optional question
 */
export const formatSkipForSubmission = (
  questionnaireItemKind: QuestionnaireCategoryItemsItem['questionnaireItemKind'],
): UpdateQuestionnaireResponseAnswerInput & { value: [DTOValueSkip] } => {
  const displayValue = questionnaireItemKind === 'interstitial' ? 'Viewed interstitial' : 'User chose to skip';
  return {
    value: [{ valueSkip: true, displayValue }],
  };
};

/**
 * Hook to get individual question with navigation context
 * Provides server-driven navigation with conditional question logic
 */
export const useGetQuestionWithNavigation = (
  questionnaireId: string,
  responseId: string,
  questionSlug: string,
  enabled = true,
) => {
  return useGetQuestionnaireResponseQuestionV2QuestionnairesQuestionnaireIdResponsesResponseIdQuestionsQuestionSlugGet(
    questionnaireId,
    responseId,
    questionSlug,
    {
      query: {
        enabled: !!questionnaireId && !!responseId && !!questionSlug && enabled,
        staleTime: 30 * 1000, // 30 seconds - navigation context can change as user answers
        retry: false,
      },
    },
  );
};

/**
 * Helper function to detect if an error is a 404 response not found error
 * Used to detect when a questionnaire response has been deleted
 */
export const isResponseNotFoundError = (error: any): boolean => {
  return error?.response?.status === 404 || error?.status === 404;
};
